// GlobalErrorBoundary (React 19) — ловит render-ошибки дерева и шлёт в Hawk
// (ТЗ §3.5). window.onerror / unhandledrejection ловит сам Hawk SDK; этот
// boundary нужен для React render-фазы (туда window.onerror не достаёт).
//
// Оборачивает <App /> в main.tsx. Fallback — минимальный, без зависимости
// от UI-кита и от живого i18next (ошибка могла случиться в провайдере темы/i18n).
// Копирайт берём из locale JSON напрямую (ключи common.errors.boundary.*).
import { Component, type ErrorInfo, type ReactNode } from "react";
import enCommon from "@/i18n/locales/en/common.json";
import ruCommon from "@/i18n/locales/ru/common.json";
import { captureClientError } from "./hawk-client";

interface Props {
	children: ReactNode;
	/** Кастомный fallback (по умолчанию — простой reload-экран). */
	fallback?: ReactNode;
}

interface State {
	hasError: boolean;
}

function boundaryCopy(): { title: string; body: string; reload: string } {
	const isRu =
		typeof navigator !== "undefined" && (navigator.language ?? "").toLowerCase().startsWith("ru");
	return (isRu ? ruCommon : enCommon).errors.boundary;
}

export class GlobalErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false };

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		captureClientError(error, {
			source: "react-error-boundary",
			componentStack: info.componentStack ?? undefined,
		});
	}

	render(): ReactNode {
		if (!this.state.hasError) return this.props.children;
		if (this.props.fallback) return this.props.fallback;
		const copy = boundaryCopy();
		return (
			<div
				role="alert"
				style={{
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					minHeight: "100dvh",
					gap: 12,
					fontFamily: "system-ui, sans-serif",
					padding: 24,
					textAlign: "center",
				}}
			>
				<h1 style={{ fontSize: 18, fontWeight: 600 }}>{copy.title}</h1>
				<p style={{ opacity: 0.7, maxWidth: 420 }}>{copy.body}</p>
				<button
					type="button"
					onClick={() => window.location.reload()}
					style={{
						padding: "8px 16px",
						borderRadius: 8,
						border: "1px solid currentColor",
						background: "transparent",
						cursor: "pointer",
						font: "inherit",
					}}
				>
					{copy.reload}
				</button>
			</div>
		);
	}
}
